import { useState, useRef, useCallback, useEffect } from 'react';
import Voice from '@react-native-voice/voice';

export type VoiceInputState = 'idle' | 'listening' | 'processing' | 'done' | 'error';

export interface UseVoiceInputOptions {
    locale?: string;
    onResult?: (text: string) => void;
    onError?: (message: string) => void;
}

export interface UseVoiceInputReturn {
    state: VoiceInputState;
    transcript: string;
    error: string | null;
    isAvailable: boolean;
    startListening: () => Promise<void>;
    stopListening: () => Promise<void>;
    reset: () => void;
}

/**
 * Hook for on-device speech-to-text using @react-native-voice/voice.
 *
 * Handles microphone permission, recording lifecycle, and result delivery.
 * Designed for single-utterance child input (tap-to-speak, auto-stop).
 *
 * Accessibility (WCAG 2.2 AA): Voice input state changes (idle → listening →
 * processing → done/error) must be announced to screen reader users so they
 * understand what the app is doing. Consuming components should use
 * `accessibilityLiveRegion="polite"` on the element that displays the current
 * `state` value (e.g. "Listening…", "Processing…", "Done") so that each
 * transition is announced. The `transcript` and `error` return values should
 * also be announced when they change, so the user knows the result or failure
 * reason without seeing the screen.
 */
export function useVoiceInput(options: UseVoiceInputOptions = {}): UseVoiceInputReturn {
    const { locale = 'en-US', onResult, onError } = options;
    const [state, setState] = useState<VoiceInputState>('idle');
    const [transcript, setTranscript] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isAvailable, setIsAvailable] = useState(false);
    const mountedRef = useRef(true);
    const listeningRef = useRef(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Store callbacks in refs so the Voice event-handler effect can run once
    // on mount without re-running when the parent component re-renders and
    // passes new inline closure references.  Previously, [onResult, onError]
    // in the dep array caused the cleanup (Voice.destroy) to fire on every
    // re-render — tearing down the active recognition session before
    // onSpeechResults could fire.
    const onResultRef = useRef(onResult);
    const onErrorRef = useRef(onError);
    useEffect(() => {
        onResultRef.current = onResult;
        onErrorRef.current = onError;
    });

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Probe whether the native speech-recognition service is available WITHOUT
    // actually starting a recognition session. Calling Voice.start() as a probe
    // triggers a real microphone session (and on devices without Google Speech
    // Services, surfaces the "service not registered" error to the user).
    //
    // Voice.isAvailable() returns Promise<0 | 1>; on Android we additionally
    // check that at least one speech recognition service is installed.
    useEffect(() => {
        let cancelled = false;
        const probe = async () => {
            const V = Voice as any;
            try {
                const available = typeof V.isAvailable === 'function' ? await V.isAvailable() : 1;
                let ok = Boolean(available);
                if (ok && typeof V.getSpeechRecognitionServices === 'function') {
                    try {
                        const services = await V.getSpeechRecognitionServices();
                        ok = Array.isArray(services) && services.length > 0;
                    } catch (probeError) {
                        console.warn('[useVoiceInput] Could not inspect speech recognition services:', probeError);
                    }
                }
                if (!cancelled) setIsAvailable(ok);
            } catch (probeError) {
                console.warn('[useVoiceInput] Speech recognition is unavailable:', probeError);
                if (!cancelled) setIsAvailable(false);
            }
        };
        probe();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        Voice.onSpeechStart = () => {
            if (mountedRef.current) {
                listeningRef.current = true;
                setState('listening');
            }
        };
        Voice.onSpeechEnd = () => {
            if (!mountedRef.current) return;
            listeningRef.current = false;
            // Only transition to 'processing' if we haven't already received
            // results.  Some native platforms fire onSpeechEnd AFTER
            // onSpeechResults, and unconditionally setting 'processing'
            // would overwrite the 'done' state and lose the result.
            setState((prev) => prev === 'done' ? prev : 'processing');
        };
        Voice.onSpeechResults = (e: any) => {
            if (!mountedRef.current) return;
            listeningRef.current = false;
            if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
            const text = String(e?.value?.[0] || '').trim();
            setTranscript(text);
            setState('done');
            try {
                if (Voice.stop) Voice.stop();
            } catch (stopError) {
                console.warn('[useVoiceInput] Could not stop after receiving a speech result:', stopError);
            }
            if (text) onResultRef.current?.(text);
        };
        Voice.onSpeechError = (e: any) => {
            if (!mountedRef.current) return;
            listeningRef.current = false;
            if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
            
            const rawMsg = e?.error?.message || e?.error || '';
            const code = String(e?.error?.code || '');
            const msgStr = String(rawMsg);
            
            // Check if this is a benign cancellation from resetting a previous session
            const isCancel = code === '5' || msgStr.toLowerCase().includes('cancel') || msgStr.includes('216');
            if (isCancel) {
                setState('idle');
                return;
            }

            const isNoMatch = code === '7' || msgStr.includes('7') || msgStr.toLowerCase().includes('no match');
            const isTimeout = code === '6' || msgStr.includes('6') || msgStr.toLowerCase().includes('timeout');

            const userMessage = isNoMatch || isTimeout
                ? 'Did not hear anything. Try again!'
                : 'Could not hear your name. Try again!';

            setError(userMessage);
            setState('error');
            onErrorRef.current?.(userMessage);
        };

        return () => {
            try {
                if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
                if (Voice.destroy) Voice.destroy();
            } catch (cleanupError) {
                console.warn('[useVoiceInput] Could not clean up speech recognition:', cleanupError);
            }
        };
    }, []);

    const startListening = useCallback(async () => {
        setError(null);
        setTranscript('');
        setState('idle');
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (!isAvailable) {
            const msg = 'Voice search is not available on this device.';
            setError(msg);
            setState('error');
            onErrorRef.current?.(msg);
            return;
        }
        try {
            // Proactively cancel/stop any lingering native session
            try {
                if (Voice.cancel) {
                    await Voice.cancel();
                } else if (Voice.stop) {
                    await Voice.stop();
                }
            } catch (cancelError) {
                console.warn('[useVoiceInput] Could not cancel the previous speech session:', cancelError);
            }

            listeningRef.current = true;
            await Voice.start(locale);
            // Safety timeout: if no result within 10 seconds, auto-stop and
            // notify the caller. This handles emulators / devices where the
            // speech service starts but never fires onSpeechResults.
            if (mountedRef.current) {
                timeoutRef.current = setTimeout(() => {
                    if (mountedRef.current && listeningRef.current) {
                        listeningRef.current = false;
                        try {
                            Voice.stop();
                        } catch (stopError) {
                            console.warn('[useVoiceInput] Could not stop timed-out recognition:', stopError);
                        }
                        if (mountedRef.current) {
                            setError('Did not hear anything. Try again.');
                            setState('error');
                            onErrorRef.current?.('Did not hear anything. Try again.');
                        }
                    }
                }, 10000);
            }
        } catch (e: any) {
            const msg = e?.message || 'Cannot start listening. Check microphone permission.';
            setError(msg);
            setState('error');
            onErrorRef.current?.(msg);
        }
    }, [locale, isAvailable]);

    const stopListening = useCallback(async () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        listeningRef.current = false;
        try {
            await Voice.stop();
        } catch (stopError) {
            console.warn('[useVoiceInput] Could not stop recognition:', stopError);
        }
    }, []);

    const reset = useCallback(() => {
        setState('idle');
        setTranscript('');
        setError(null);
    }, []);

    return { state, transcript, error, isAvailable, startListening, stopListening, reset };
}
